import { CurrencyPipe, DatePipe, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { CognitoAuthService, WINDOW } from '@techspert-io/auth';
import {
  CallSolicitationOutcomeDialogComponent,
  CallSolicitationsListDialogComponent,
  CallSolicitationsService,
  ICallSolicitation,
  ICallSolicitationCreate,
  ICallSolicitationOutcomeDialogData,
  ICallSolicitationUpdateReturn,
  ICallSolicitationsListDialogData,
  buildTeamsCallUrl,
  normaliseTeamsPhoneNumber,
} from '@techspert-io/call-solicitations';
import { IOpportunity } from '@techspert-io/opportunities';
import {
  ConfirmationDialogComponent,
  IConfirmationDialogData,
  IProfileNotesDialogData,
  InitialsAvatarComponent,
  ProfileNotesDialogComponent,
} from '@techspert-io/shared';
import { ToastService } from '@techspert-io/user-alerts';
import { EMPTY, catchError, filter, map, of, switchMap, takeWhile } from 'rxjs';
import { MaterialModule } from '../../../../shared/patterns/material-module';
import { IOpportunitySegmentData } from '../../../opportunity-segments/models/opportunity-segments.models';
import { OpportunitySegmentsService } from '../../../opportunity-segments/services/opportunity-segments.service';
import {
  IExpertEmailData,
  IOutreachEmailRes,
  OutreachEmailDialogComponent,
} from '../../../outreach-email';
import { EnrichedUser } from '../../../users/models/user.models';
import { UserService } from '../../../users/services/user.service';
import {
  IMvpExpertFilter,
  IMvpExpertView,
  IMvpExpertViewEnriched,
  MvpExpertOutreachType,
  MvpExpertProgression,
} from '../../models/mvp-experts.models';
import {
  MvpExpertTypePipe,
  MvpExpertTypeTooltipPipe,
} from '../../pipes/mvp-expert-type.pipe';
import { MvpExpertsService } from '../../services/mvp-experts.service';
import { MvpExpertsFilterComponent } from '../mvp-experts-filter/mvp-experts-filter.component';

@Component({
  selector: 'app-mvp-experts-list',
  templateUrl: './mvp-experts-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CurrencyPipe,
    DatePipe,
    TitleCasePipe,
    RouterModule,
    MaterialModule,
    InitialsAvatarComponent,
    MvpExpertsFilterComponent,
    MvpExpertTypePipe,
    MvpExpertTypeTooltipPipe,
  ],
})
export class MvpExpertsListComponent {
  private readonly mvpExpertsService = inject(MvpExpertsService);
  private readonly callSolicitationsService = inject(CallSolicitationsService);
  private readonly toastService = inject(ToastService);
  private readonly userService = inject(UserService);
  private readonly cognitoAuthService = inject(CognitoAuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly window = inject(WINDOW);
  private readonly opportunitySegmentsService = inject(
    OpportunitySegmentsService
  );
  readonly dialog = inject(MatDialog);

  readonly opportunity = input.required<IOpportunity>();

  readonly experts = rxResource<IMvpExpertViewEnriched[], string>({
    params: () => this.opportunity().opportunityId,
    stream: ({ params: opportunityId }) =>
      this.mvpExpertsService.getMvpExperts(opportunityId).pipe(
        map((experts) =>
          experts.map((expert) => ({
            ...expert,
            jobHistory: [...expert.jobHistory].sort((a, b) => {
              if (!a.endDate && b.endDate) return -1;
              if (a.endDate && !b.endDate) return 1;
              return (
                new Date(b.startDate).getTime() -
                new Date(a.startDate).getTime()
              );
            }),
            progression: this.getProgression(expert),
            searchString: [
              expert.firstName,
              expert.lastName,
              expert.primaryEmail,
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase(),
          }))
        ),
        catchError(() => {
          this.toastService.sendMessage('Failed to load MVP experts', 'error');
          return of([]);
        })
      ),
  });

  readonly internalUsers = rxResource<Record<string, EnrichedUser>, void>({
    stream: () => this.userService.getInternalUsersDictionary(),
  });

  readonly segments = rxResource<
    Record<string, IOpportunitySegmentData>,
    string
  >({
    params: () => this.opportunity().opportunityId,
    stream: ({ params: opportunityId }) =>
      this.opportunitySegmentsService.queryByOpportunityId(opportunityId).pipe(
        map((segs) =>
          segs.reduce<Record<string, IOpportunitySegmentData>>((acc, seg) => {
            acc[seg.opportunitySegmentId] = seg;
            return acc;
          }, {})
        )
      ),
  });

  readonly filter = signal<IMvpExpertFilter>({
    search: '',
    countries: [],
    currentJobOnly: false,
    companies: [],
    industries: [],
    jobTitles: [],
    types: [],
    ownerConnectIds: [],
    opportunitySegmentIds: [],
    progressions: [],
    honorariaCurrency: null,
    honorariaAmount: null,
    honorariaOperator: null,
  });

  readonly filteredExperts = computed(() =>
    this.applyFilter(this.experts.value() ?? [], this.filter())
  );

  openCallSolicitationsDialog(expert: IMvpExpertViewEnriched): void {
    this.dialog.open<
      CallSolicitationsListDialogComponent,
      ICallSolicitationsListDialogData
    >(CallSolicitationsListDialogComponent, {
      data: {
        expertId: expert.opportunityExpertId,
        expertName: this.getExpertName(expert) || 'Expert',
      },
      width: '900px',
      maxWidth: '95vw',
      maxHeight: '86vh',
    });
  }

  openProfileNotesDialog(expert: IMvpExpertViewEnriched) {
    const name = [expert.firstName, expert.lastName].filter(Boolean).join(' ');
    this.dialog.open<ProfileNotesDialogComponent, IProfileNotesDialogData>(
      ProfileNotesDialogComponent,
      {
        width: '640px',
        maxWidth: '90vw',
        data: {
          notes: expert.pinnedNotes,
          title: name ? `Pinned notes - ${name}` : 'Pinned notes',
        },
      }
    );
  }

  openEmailDialog(expert: IMvpExpertViewEnriched): void {
    const opp = this.opportunity();
    const projectType = opp?.projectType;
    const honorariaAmount =
      projectType === 'Quant'
        ? expert.honoraria?.quant
        : projectType === 'Qual'
          ? expert.honoraria?.qual
          : undefined;

    const emailData: IExpertEmailData = {
      expertId: expert.opportunityExpertId,
      emails: expert.opportunityEmails.map((e) => ({
        address: e,
        isPrimary: e === expert.primaryEmail,
      })),
      opporunitySegmentId: expert.opportunitySegmentId,
      expertName: [expert.firstName, expert.lastName].filter(Boolean).join(' '),
      ...(expert.honoraria &&
        honorariaAmount !== undefined && {
          honoraria: {
            currency: expert.honoraria.currency,
            amount: honorariaAmount,
          },
        }),
    };

    this.dialog
      .open<OutreachEmailDialogComponent, IExpertEmailData, IOutreachEmailRes>(
        OutreachEmailDialogComponent,
        {
          data: emailData,
          width: '90vw',
          height: '90vh',
        }
      )
      .afterClosed()
      .pipe(takeWhile((res) => !!res))
      .subscribe((res) => {
        this.toastService.sendMessage('Email queued successfully', 'success');

        if (res?.expert && Object.keys(res.expert).length) {
          this.experts.update(
            (experts) =>
              experts.map((e) => {
                if (e.opportunityExpertId !== expert.opportunityExpertId) {
                  return e;
                }

                const updatedPhase = res.expert.connectPhase ?? e.connectPhase;
                const updatedTypes: MvpExpertOutreachType[] =
                  e.attemptedOutreachTypes.includes('email')
                    ? e.attemptedOutreachTypes
                    : [...e.attemptedOutreachTypes, 'email'];

                return {
                  ...e,
                  connectPhase: updatedPhase,
                  attemptedOutreachTypes: updatedTypes,
                  progression: this.getProgression({
                    ...e,
                    connectPhase: updatedPhase,
                    attemptedOutreachTypes: updatedTypes,
                  }),
                };
              }) ?? []
          );
        }
      });
  }

  openCallDialog(expert: IMvpExpertViewEnriched): void {
    const teamsPhoneNumber = normaliseTeamsPhoneNumber(
      expert.primaryPhoneNumber,
      expert.country
    );
    if (!teamsPhoneNumber) {
      this.toastService.sendMessage(
        'Phone number must include an international country code before calling via Teams',
        'error'
      );
      return;
    }

    const callerConnectId = this.cognitoAuthService.loggedInUser?.user_id;
    if (!callerConnectId) {
      this.toastService.sendMessage(
        'Could not create call record because the current user is unavailable',
        'error'
      );
      return;
    }

    const expertName = this.getExpertName(expert);

    this.dialog
      .open<ConfirmationDialogComponent, IConfirmationDialogData, boolean>(
        ConfirmationDialogComponent,
        {
          data: {
            title: 'Call expert',
            messages: [
              `Create a call record and call ${expertName || 'this expert'} on ${expert.primaryPhoneNumber} via Microsoft Teams.`,
            ],
            positiveButtonText: 'Call expert',
            negativeButtonText: 'Cancel',
          },
        }
      )
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(Boolean),
        switchMap(() => {
          const teamsWindow = this.window.open('', '_blank');
          if (teamsWindow) {
            teamsWindow.opener = null;
          }

          const request: ICallSolicitationCreate = {
            recipient_number: teamsPhoneNumber,
            expert_id: expert.opportunityExpertId,
            caller_connect_id: callerConnectId,
            outcome: null,
            notes: null,
            duration_seconds: null,
          };

          return this.callSolicitationsService
            .createCallSolicitation(request)
            .pipe(
              map((solicitation) => ({
                solicitation,
                teamsWindow,
              })),
              catchError(() => {
                teamsWindow?.close();
                this.toastService.sendMessage(
                  'Failed to create call record',
                  'error'
                );
                return EMPTY;
              })
            );
        })
      )
      .subscribe(({ solicitation, teamsWindow }) => {
        this.openTeamsCall(
          buildTeamsCallUrl(
            teamsPhoneNumber,
            [expertName || 'Expert', this.opportunity().opportunityName]
              .filter(Boolean)
              .join(' - ')
          ),
          teamsWindow
        );
        this.openCallOutcomeDialog(solicitation, expert, teamsPhoneNumber);
        this.markCallAttempted(expert);
      });
  }

  private getProgression(expert: IMvpExpertView): MvpExpertProgression {
    if (expert.rejection && Object.keys(expert.rejection).length > 0) {
      return 'rejected';
    }

    switch (expert.connectPhase) {
      case 'firstFollowUp':
      case 'secondFollowUp':
      case 'outreachComplete':
        return 'solicitation';
      case 'outreach':
        return 'responded';
      case 'screener':
        return 'screening';
      case 'sentToClient':
      case 'accepted':
        return 'client portal';
      case 'scheduled':
        return 'scheduled';
      case 'completed':
        return 'complete';
      default:
        return expert.attemptedOutreachTypes.length
          ? 'solicitation'
          : 'identified';
    }
  }

  private getExpertName(expert: IMvpExpertView): string {
    return [expert.firstName, expert.lastName].filter(Boolean).join(' ');
  }

  private openTeamsCall(teamsUrl: string, teamsWindow: Window | null): void {
    if (teamsWindow) {
      teamsWindow.location.href = teamsUrl;
      return;
    }

    const openedWindow = this.window.open(
      teamsUrl,
      '_blank',
      'noopener,noreferrer'
    );
    if (!openedWindow) {
      this.toastService.sendMessage(
        'Teams call window was blocked by the browser',
        'error'
      );
    }
  }

  private openCallOutcomeDialog(
    solicitation: ICallSolicitation,
    expert: IMvpExpertViewEnriched,
    teamsPhoneNumber: string
  ): void {
    const opportunity = this.opportunity();
    const segment = this.segments.value()?.[expert.opportunitySegmentId];
    const honoraria = expert.honoraria;
    const latestJob = expert.jobHistory[0];
    const data: ICallSolicitationOutcomeDialogData = {
      solicitation,
      expert: {
        expertId: expert.opportunityExpertId,
        profileId: expert.profileId,
        name: this.getExpertName(expert) || 'Missing name',
        primaryEmail: expert.primaryEmail,
        primaryPhoneNumber: teamsPhoneNumber,
        country: expert.country,
        linkedin: expert.linkedin,
        ...(latestJob && {
          latestJob: {
            jobTitle: latestJob.jobTitle,
            company: latestJob.company,
            industry: latestJob.industry,
          },
        }),
        ...(honoraria && { honoraria }),
      },
      opportunity: {
        opportunityId: opportunity.opportunityId,
        opportunityName: opportunity.opportunityName,
        projectType: opportunity.projectType,
        anticipatedCallTime: opportunity.anticipatedCallTime,
      },
      ...(segment && {
        segment: {
          geography: segment.geography,
          segment: segment.segment,
        },
      }),
      pinnedNotes: expert.pinnedNotes.map((note) => note.note).filter(Boolean),
    };

    this.dialog
      .open<
        CallSolicitationOutcomeDialogComponent,
        ICallSolicitationOutcomeDialogData,
        ICallSolicitationUpdateReturn
      >(CallSolicitationOutcomeDialogComponent, {
        width: '1120px',
        maxWidth: '98vw',
        maxHeight: '86vh',
        panelClass: 'call-solicitation-outcome-dialog-panel',
        data,
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((result): result is ICallSolicitationUpdateReturn =>
          Boolean(result)
        )
      )
      .subscribe((result) =>
        this.updateExpertFromCallSolicitationOutcome(result)
      );
  }

  private markCallAttempted(expert: IMvpExpertViewEnriched): void {
    this.experts.update(
      (experts) =>
        experts.map((existingExpert) => {
          if (
            existingExpert.opportunityExpertId !== expert.opportunityExpertId
          ) {
            return existingExpert;
          }

          const attemptedOutreachTypes: MvpExpertOutreachType[] =
            existingExpert.attemptedOutreachTypes.includes('call')
              ? existingExpert.attemptedOutreachTypes
              : [...existingExpert.attemptedOutreachTypes, 'call'];

          return {
            ...existingExpert,
            attemptedOutreachTypes,
            progression: this.getProgression({
              ...existingExpert,
              attemptedOutreachTypes,
            }),
          };
        }) ?? []
    );
  }

  private updateExpertFromCallSolicitationOutcome(
    result: ICallSolicitationUpdateReturn
  ): void {
    const { expert_update: expertUpdate, profile_update: profileUpdate } =
      result;

    if (!expertUpdate && !profileUpdate) {
      return;
    }

    this.experts.update(
      (experts) =>
        experts.map((existingExpert) => {
          let updated: IMvpExpertViewEnriched = existingExpert;

          if (
            expertUpdate &&
            existingExpert.opportunityExpertId === expertUpdate.expertId
          ) {
            const rejection =
              expertUpdate.rejection === undefined
                ? existingExpert.rejection
                : {
                    ...existingExpert.rejection,
                    ...expertUpdate.rejection,
                  };

            updated = {
              ...updated,
              connectPhase: expertUpdate.connectPhase ?? updated.connectPhase,
              rejection,
            };
          }

          if (
            profileUpdate?.honorarium &&
            existingExpert.profileId === profileUpdate.expertProfileId
          ) {
            updated = { ...updated, honoraria: profileUpdate.honorarium };
          }

          if (updated === existingExpert) {
            return existingExpert;
          }

          return { ...updated, progression: this.getProgression(updated) };
        }) ?? []
    );
  }

  private applyFilter(
    experts: IMvpExpertViewEnriched[],
    filter: IMvpExpertFilter
  ): IMvpExpertViewEnriched[] {
    const searchTerm = filter.search.trim().toLowerCase();
    const hasJobFilter =
      filter.currentJobOnly ||
      filter.companies.length > 0 ||
      filter.industries.length > 0 ||
      filter.jobTitles.length > 0;

    return experts.filter((expert) => {
      if (searchTerm && !expert.searchString.includes(searchTerm)) {
        return false;
      }

      if (
        filter.countries.length &&
        (!expert.country || !filter.countries.includes(expert.country))
      ) {
        return false;
      }

      if (
        filter.types.length &&
        !filter.types.some((type) => expert.mvpTypes.includes(type))
      ) {
        return false;
      }

      if (
        filter.ownerConnectIds.length &&
        !filter.ownerConnectIds.includes(expert.ownerConnectId)
      ) {
        return false;
      }

      if (
        filter.opportunitySegmentIds.length &&
        !filter.opportunitySegmentIds.includes(expert.opportunitySegmentId)
      ) {
        return false;
      }

      if (hasJobFilter) {
        const hasMatchingJob = expert.jobHistory.some((job) => {
          if (filter.currentJobOnly && job.endDate) {
            return false;
          }
          if (
            filter.companies.length &&
            !filter.companies.includes(job.company)
          ) {
            return false;
          }
          if (
            filter.industries.length &&
            (!job.industry || !filter.industries.includes(job.industry))
          ) {
            return false;
          }
          if (
            filter.jobTitles.length &&
            !filter.jobTitles.includes(job.jobTitle)
          ) {
            return false;
          }

          return true;
        });

        if (!hasMatchingJob) {
          return false;
        }
      }

      if (
        filter.progressions.length &&
        !filter.progressions.includes(expert.progression)
      ) {
        return false;
      }

      if (filter.honorariaCurrency) {
        const expHon = expert.honoraria;
        if (!expHon || expHon.currency !== filter.honorariaCurrency) {
          return false;
        }

        if (
          filter.honorariaOperator !== null &&
          filter.honorariaAmount !== null
        ) {
          const amounts = (
            [expHon.quant, expHon.qual] as (number | undefined)[]
          ).filter((v): v is number => v !== undefined);
          const passes = amounts.some((amt) => {
            switch (filter.honorariaOperator) {
              case 'gt':
                return amt > filter.honorariaAmount;
              case 'gte':
                return amt >= filter.honorariaAmount;
              case 'lt':
                return amt < filter.honorariaAmount;
              case 'lte':
                return amt <= filter.honorariaAmount;
              case 'eq':
                return amt === filter.honorariaAmount;
            }
          });

          if (!passes) {
            return false;
          }
        }
      }

      return true;
    });
  }
}
